import { useDrag } from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { PageContext } from '@context/page';
import { PagesContext } from '@context/pages';
import { PublishContext } from '@context/publish';
import { Block } from '@typings/block';
import { Page } from '@typings/page';

import { PagesEditPage } from './pages-edit-page';
import { PagesEditSection } from './pages-edit-section';

interface PagesDragItemProps extends HTMLAttributes<HTMLDivElement> {
  block?: Block;
  page?: Page;
  sectionBlocks?: Block[];
}

const UnstyledPagesDragItem: FunctionComponent<PagesDragItemProps> = (props) => {
  const { block, children, page, sectionBlocks = [], ...otherProps } = props;

  const ref = useRef<HTMLDivElement | null>(null);

  const { pages } = useContext(PageContext);
  const {
    draggingPage,
    draggingSection,
    editingSections,
    movingPage,
    setDraggingPage,
    setDraggingSection
  } = useContext(PagesContext);
  const { publishing } = useContext(PublishContext);

  const format = page ? 'page' : block?.pageKey || '';
  const key = block?.key || page?.key || '';
  const only = (block && sectionBlocks.length < 2) || (page && pages.length < 2) || false;

  const getDisabled = () => {
    if (!format) return true;
    if (movingPage) return true;
    if (editingSections.length > 0) return true;
    if (publishing) return true;
    if (only) return true;
    if (block || page) return false;
    return true;
  };

  const disabled = getDisabled();

  const { dragging } = useDrag({
    data: key,
    disabled,
    format,
    ref
  });

  useEffect(() => {
    if (dragging) {
      if (block) {
        setDraggingSection(block);
        setDraggingPage(null);
      } else if (page) {
        setDraggingPage(page);
        setDraggingSection(null);
      }
    } else {
      setDraggingPage(null);
      setDraggingSection(null);
    }
  }, [dragging]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!block && !page) return null;

  return (
    <div
      {...otherProps}
      className={classNames({
        'pages-drag-item': true,
        'pages-drag-item--block': !!block,
        'pages-drag-item--disabled': disabled,
        'pages-drag-item--dragging':
          dragging && (block?.key === draggingSection?.key || page?.key === draggingPage?.key),
        'pages-drag-item--only': only,
        'pages-drag-item--page': !!page,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      data-testid="drag-item"
      ref={ref}
    >
      {page && <PagesEditPage page={page} />}
      {block && <PagesEditSection block={block} sectionBlocks={sectionBlocks} />}
      {page && children}
    </div>
  );
};

const StyledPagesDragItem = styled(UnstyledPagesDragItem)`
  cursor: grab;

  &.pages-drag-item--disabled {
    cursor: default;
  }

  &.pages-drag-item--dragging {
    cursor: grabbing;
    opacity: 0.5;
  }

  &.pages-drag-item--only {
    .pages-drag-item__text {
      margin-left: 9px;
    }
  }
`;

export const PagesDragItem: FunctionComponent<PagesDragItemProps> = (props) => {
  return <StyledPagesDragItem {...props} />;
};
